import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Dialog } from '@headlessui/react';
const { Panel: DialogPanel, Title: DialogTitle } = Dialog;

import { XMarkIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { inventoryService } from '@/services/inventory/inventory-service';
import { InventoryItem } from '@/types';
import { useInstrumentMappings, getCounterpartyProductId } from '@/hooks/settings';

const MAX_SELECTIONS = 10;
const IS_MAC = /mac/i.test(navigator.userAgent);
const SKELETON_ROWS = 6;

interface SelectedEntry {
  item: InventoryItem;
  productId: string;
  quantity: number;
}

interface FilteredEntry {
  item: InventoryItem;
  productId: string;
}

interface AddPositionDialogProps {
  show: boolean;
  inventoryState: number | undefined;
  inventoryCounterparty?: string;
  counterparty: string;
  quantityField?: 'quantity' | 'velocity_l_quantity';
  existingProductIds: string[];
  onAdd: (counterpartyProductId: string, symbolId: string, quantity: number) => void;
  onCancel: () => void;
}

const SkeletonRow: React.FC = () => (
  <div className="flex items-center px-3 py-1.5 border-b border-gray-100 last:border-b-0 animate-pulse">
    <span className="w-2/5"><span className="block h-3.5 w-20 bg-gray-200 rounded" /></span>
    <span className="w-2/5"><span className="block h-3.5 w-24 bg-gray-200 rounded" /></span>
    <span className="w-1/5 flex justify-end"><span className="block h-3.5 w-12 bg-gray-200 rounded" /></span>
  </div>
);

const RowButton: React.FC<{
  entry: FilteredEntry;
  isSelected: boolean;
  atLimit: boolean;
  getQty: (item: InventoryItem) => number;
  onClick: (item: InventoryItem, productId: string, e: React.MouseEvent) => void;
}> = React.memo(({ entry, isSelected, atLimit, getQty, onClick }) => (
  <button
    onClick={(e) => onClick(entry.item, entry.productId, e)}
    disabled={atLimit}
    className={`w-full flex items-center px-3 py-1.5 text-sm text-left border-b border-gray-100 last:border-b-0 ${
      isSelected
        ? 'bg-blue-50 border-blue-200'
        : atLimit
          ? 'opacity-40 cursor-not-allowed'
          : 'hover:bg-gray-50'
    }`}
  >
    <span className="w-2/5 font-medium text-gray-900 truncate">{entry.productId}</span>
    <span className="w-2/5 text-gray-600 truncate">{entry.item.symbol_id}</span>
    <span className="w-1/5 text-right text-gray-500">
      {getQty(entry.item).toLocaleString('en-US')}
    </span>
  </button>
));
RowButton.displayName = 'RowButton';

export const AddPositionDialog: React.FC<AddPositionDialogProps> = ({
  show,
  inventoryState,
  inventoryCounterparty,
  counterparty,
  quantityField = 'quantity',
  existingProductIds,
  onAdd,
  onCancel,
}) => {
  const [search, setSearch] = useState('');
  const [selections, setSelections] = useState<SelectedEntry[]>([]);

  const { data: instrumentMapping } = useInstrumentMappings(counterparty, show);

  const getQty = useCallback((item: InventoryItem) => {
    if (quantityField === 'velocity_l_quantity') return item.velocity_l_quantity ?? 0;
    return item.quantity ?? 0;
  }, [quantityField]);

  useEffect(() => {
    if (!show) {
      setSearch('');
      setSelections([]);
    }
  }, [show]);

  const { data: inventoryItems, isLoading } = useQuery({
    queryKey: ['inventory', 'addPosition', inventoryState, inventoryCounterparty],
    queryFn: () => inventoryService.getAllInventory({ state: inventoryState, counterparty: inventoryCounterparty }),
    enabled: show && !!inventoryState,
    staleTime: 5 * 60 * 1000,
  });

  const existingSet = useMemo(
    () => new Set(existingProductIds),
    [existingProductIds],
  );

  const filteredItems = useMemo(() => {
    if (!inventoryItems) return [];
    const q = search.toLowerCase();
    const result: FilteredEntry[] = [];
    for (const item of inventoryItems) {
      const productId = getCounterpartyProductId(instrumentMapping, item.symbol_id);
      if (existingSet.has(productId)) continue;
      if (q && !item.symbol_id.toLowerCase().includes(q) && !productId.toLowerCase().includes(q)) continue;
      result.push({ item, productId });
    }
    return result;
  }, [inventoryItems, existingSet, search, instrumentMapping]);

  const selectedSymbolIds = useMemo(
    () => new Set(selections.map((s) => s.item.symbol_id)),
    [selections],
  );

  const handleRowClick = useCallback(
    (item: InventoryItem, productId: string, e: React.MouseEvent) => {
      const multiKey = IS_MAC ? e.metaKey : e.ctrlKey;

      setSelections((prev) => {
        const exists = prev.findIndex((s) => s.item.symbol_id === item.symbol_id);

        if (multiKey) {
          if (exists !== -1) {
            return prev.filter((_, i) => i !== exists);
          }
          if (prev.length >= MAX_SELECTIONS) return prev;
          return [...prev, { item, productId, quantity: 1 }];
        }

        if (prev.length === 1 && exists === 0) {
          return [];
        }
        return [{ item, productId, quantity: 1 }];
      });
    },
    [],
  );

  const updateQuantity = useCallback((symbolId: string, qty: number) => {
    setSelections((prev) =>
      prev.map((s) => (s.item.symbol_id === symbolId ? { ...s, quantity: qty } : s)),
    );
  }, []);

  const setAllQuantity = useCallback((symbolId: string) => {
    setSelections((prev) =>
      prev.map((s) => {
        if (s.item.symbol_id !== symbolId) return s;
        const max = getQty(s.item);
        return { ...s, quantity: max };
      }),
    );
  }, [getQty]);

  const removeSelection = useCallback((symbolId: string) => {
    setSelections((prev) => prev.filter((s) => s.item.symbol_id !== symbolId));
  }, []);

  const canAdd = selections.length > 0 && selections.every((s) => s.quantity >= 1);

  const handleAdd = () => {
    for (const s of selections) {
      onAdd(s.productId, s.item.symbol_id, s.quantity);
    }
    onCancel();
  };


  if (!show) return null;

  return (
      <Dialog as="div" className="relative z-[110]" open={show} onClose={onCancel}>
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
              <DialogPanel className="w-full max-w-lg overflow-hidden rounded-lg bg-white shadow-xl">
                <div className="p-6">
                  <DialogTitle className="text-lg font-semibold text-gray-900 mb-1">
                    Add Position
                  </DialogTitle>
                  <p className="text-xs text-gray-400 mb-3">
                    Hold {IS_MAC ? '\u2318 Cmd' : 'Ctrl'} to select multiple (max {MAX_SELECTIONS})
                  </p>

                  {/* Search input */}
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by Symbol ID or Product ID..."
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 mb-3"
                  />

                  {/* Availability list */}
                  {isLoading ? (
                    <div className="border border-gray-200 rounded mb-3">
                      <div className="flex items-center px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide bg-gray-50 border-b border-gray-200">
                        <span className="w-2/5">Product ID</span>
                        <span className="w-2/5">Symbol ID</span>
                        <span className="w-1/5 text-right">Qty</span>
                      </div>
                      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                        <SkeletonRow key={i} />
                      ))}
                    </div>
                  ) : filteredItems.length === 0 ? (
                    <div className="py-8 text-center text-sm text-gray-500">
                      No available positions found
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded mb-3">
                      <div className="flex items-center px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide bg-gray-50 border-b border-gray-200">
                        <span className="w-2/5">Product ID</span>
                        <span className="w-2/5">Symbol ID</span>
                        <span className="w-1/5 text-right">Qty</span>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {filteredItems.map((entry, index) => {
                          const isSelected = selectedSymbolIds.has(entry.item.symbol_id);
                          const atLimit = selections.length >= MAX_SELECTIONS && !isSelected;
                          return (
                            <RowButton
                              key={`${entry.item.symbol_id}-${index}`}
                              entry={entry}
                              isSelected={isSelected}
                              atLimit={atLimit}
                              getQty={getQty}
                              onClick={handleRowClick}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Selected positions with quantity inputs */}
                  {selections.length > 0 && (
                    <div className="mb-4">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                        Selected ({selections.length})
                      </div>
                      <div className="space-y-1.5">
                        {selections.map((s) => {
                          const maxQty = getQty(s.item);
                          return (
                            <div key={s.item.symbol_id} className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 w-28 truncate" title={s.productId}>
                                {s.productId}
                              </span>
                              <input
                                type="number"
                                min={1}
                                value={s.quantity}
                                onChange={(e) =>
                                  updateQuantity(s.item.symbol_id, e.target.value === '' ? 0 : Number(e.target.value))
                                }
                                className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              />
                              <button
                                type="button"
                                onClick={() => setAllQuantity(s.item.symbol_id)}
                                title={`Set to ${maxQty.toLocaleString('en-US')}`}
                                className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded border border-blue-200 hover:bg-blue-100"
                              >
                                All
                              </button>
                              <button
                                type="button"
                                onClick={() => removeSelection(s.item.symbol_id)}
                                className="p-1 text-gray-400 hover:text-red-500"
                                title="Remove"
                              >
                                <XMarkIcon className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={onCancel}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAdd}
                      disabled={!canAdd}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {selections.length > 1 ? `Add ${selections.length}` : 'Add'}
                    </button>
                  </div>
                </div>
              </DialogPanel>
          </div>
        </div>
      </Dialog>
  );
};

export default AddPositionDialog;
